import React, { memo, useEffect, useState } from 'react';
import CommentComposer from './CommentComposer';
import { CommentImage, CommentNodeProps } from '../interfaces';
import { formatRelativeTime } from './formatRelativeTime';

const CommentNode = memo(({ item, userId, productId, depth = 0 }: CommentNodeProps) => {
    const [isReplying, setIsReplying] = useState(false);
    const [activeImage, setActiveImage] = useState<CommentImage | null>(null);
    const authorName = item.author?.name || item.user?.name || 'Anonymous';
    const imageItems = item.images?.filter((commentImage) => commentImage.image?.publicUrl) || [];

    useEffect(() => {
        if (!activeImage) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setActiveImage(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeImage]);

    return (
        <div className="comment-thread-node" style={{ marginLeft: depth ? 18 : 0 }}>
            <div className="comment-item">
                <div className="comment-block">
                    <div className="comment-header-row">
                        <h5 className="user-name">{authorName}</h5>
                        {item.createdAt && (
                            <span className="comment-date">
                                {formatRelativeTime(item.createdAt)}
                            </span>
                        )}
                    </div>
                    <p className="comment-content">{item.content}</p>
                    {imageItems.length > 0 && (
                        <div className="comment-image-grid">
                            {imageItems.map((commentImage) => (
                                <button
                                    key={commentImage.id}
                                    type="button"
                                    className="comment-image-thumb"
                                    onClick={() => setActiveImage(commentImage)}
                                >
                                    <img
                                        src={commentImage.image?.publicUrl}
                                        alt={commentImage.image?.originalFilename || 'Comment attachment'}
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                    {userId && productId && (
                        <button
                            type="button"
                            className="reply-toggle-button"
                            onClick={() => setIsReplying((value) => !value)}
                        >
                            {isReplying ? 'Cancel reply' : 'Reply'}
                        </button>
                    )}
                </div>
                {isReplying && (
                    <CommentComposer
                        userId={userId}
                        productId={productId}
                        parentId={item.id}
                        buttonLabel="Add Reply"
                        placeholder="Write a reply..."
                        onSubmitted={() => setIsReplying(false)}
                    />
                )}
            </div>
            {item.replies.length > 0 && (
                <div className="comment-replies">
                    {item.replies.map((reply) => (
                        <CommentNode
                            key={reply.id}
                            item={reply}
                            userId={userId}
                            productId={productId}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
            {activeImage?.image?.publicUrl && (
                <div
                    className="comment-image-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Comment image preview"
                    onClick={() => setActiveImage(null)}
                >
                    <div className="comment-image-modal__content" onClick={(event) => event.stopPropagation()}>
                        <button
                            type="button"
                            className="comment-image-modal__close"
                            aria-label="Close image preview"
                            onClick={() => setActiveImage(null)}
                        >
                            ×
                        </button>
                        <img
                            src={activeImage.image.publicUrl}
                            alt={activeImage.image.originalFilename || 'Comment attachment'}
                        />
                    </div>
                </div>
            )}
        </div>
    );
});

export default CommentNode;
